import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Form, Spinner, Table } from "react-bootstrap";
import API from "../services/api";
import "./AdminSupplierPoView.css";
import { downloadSupplierPoPdf } from "../utils/pdfDocs";
import { formatSupplierId } from "../utils/idFormat";

const fmtIST = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

const AdminSupplierPoView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const poNumber = searchParams.get("poNumber") || "";
      const res = await API.get(`/admin/suppliers/${id}/po-details`, {
        params: { poNumber: poNumber || undefined },
      });
      setData(res.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load supplier PO details");
    } finally {
      setLoading(false);
    }
  }, [id, searchParams]);

  useEffect(() => {
    load();
  }, [load]);

  const po = data?.po || {};
  const summary = data?.summary || {};
  const poHistory = Array.isArray(data?.poHistory) ? data.poHistory : [];
  const supplier = data?.supplier || {};
  const profile = data?.profile || {};
  const supplierDisplayId = formatSupplierId(profile, supplier);
  const unit = po.unit || "ton";
  const selectedPoNumber = po.poNumber || searchParams.get("poNumber") || "";

  const selectPo = (nextPoNumber) => {
    const next = nextPoNumber
      ? `/admin/suppliers/${id}/po?poNumber=${encodeURIComponent(nextPoNumber)}`
      : `/admin/suppliers/${id}/po`;
    navigate(next);
  };

  const paidAmount = useMemo(() => {
    const v = num(po.paidAmount);
    return v == null ? "—" : `₹${v.toLocaleString("en-IN")}`;
  }, [po.paidAmount]);

  if (loading) {
    return (
      <div className="apo-wrap text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="apo-wrap">
        <Alert variant="danger" className="mt-3">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate("/admin/suppliers")}>Back to Suppliers</Button>
      </div>
    );
  }

  return (
    <div className="apo-wrap">
      <div className="apo-top">
        <div>
          <h4 className="apo-title mb-1">Supplier PO Details</h4>
          <div className="apo-sub">
            {(profile.companyName || profile.supplierName || supplier.fullName || "Supplier")} | {supplierDisplayId || "No code"}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-dark"
            onClick={() => downloadSupplierPoPdf({ profile: { ...profile, po }, dispatches: data?.dispatches || [] })}
          >
            Download PO PDF
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate(`/admin/suppliers/${id}/view`)}>Supplier Profile</Button>
          <Button variant="secondary" onClick={() => navigate("/admin/suppliers")}>Back</Button>
        </div>
      </div>

      {poHistory.length > 0 ? (
        <div className="apo-table-card mb-3">
          <div className="apo-table-head">
            <div>
              <h6 className="mb-1">Select PO Number</h6>
              <div className="small text-muted">Dropdown se PO select karte hi us PO ka summary aur dispatch entries dikhenge.</div>
            </div>
            <Form.Select
              value={selectedPoNumber}
              onChange={(e) => selectPo(e.target.value)}
              style={{ maxWidth: 360 }}
            >
              {poHistory.map((row) => (
                <option key={row.poNumber || row.batchNumber} value={row.poNumber || ""}>
                  {row.poNumber || "No PO"} | {row.productName || "No product"} | {row.status === "open" ? "Open" : "Closed"} | Rem: {num(row.remainingQty) == null ? "—" : `${Number(row.remainingQty)} ${row.unit || unit}`}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>
      ) : null}

      <div className="apo-grid">
        <div className="apo-card">
          <div className="apo-k">PO Number</div>
          <div className="apo-v mono">{po.poNumber || "—"}</div>
        </div>
        <div className="apo-card">
          <div className="apo-k">Batch Number</div>
          <div className="apo-v mono">{po.batchNumber || "—"}</div>
        </div>
        <div className="apo-card">
          <div className="apo-k">Product</div>
          <div className="apo-v">{po.productName || "—"}</div>
        </div>
        <div className="apo-card">
          <div className="apo-k">Total Qty</div>
          <div className="apo-v">{num(po.totalQty) == null ? "—" : `${Number(po.totalQty)} ${unit}`}</div>
        </div>
        <div className="apo-card">
          <div className="apo-k">Received Qty</div>
          <div className="apo-v apo-green">{num(summary.receivedQty) == null ? "—" : `${Number(summary.receivedQty)} ${unit}`}</div>
        </div>
        <div className="apo-card">
          <div className="apo-k">Remaining Qty</div>
          <div className="apo-v apo-red">{num(summary.remainingQty) == null ? "—" : `${Number(summary.remainingQty)} ${unit}`}</div>
        </div>
        <div className="apo-card">
          <div className="apo-k">Received %</div>
          <div className="apo-v">{num(summary.receivedPercent) == null ? "—" : `${Number(summary.receivedPercent)}%`}</div>
        </div>
        <div className="apo-card">
          <div className="apo-k">Paid Amount</div>
          <div className="apo-v">{paidAmount}</div>
        </div>
        <div className="apo-card">
          <div className="apo-k">Status Counts</div>
          <div className="apo-v small">
            Approved: {summary.approved || 0} | Pending: {summary.pending || 0} | Rejected: {summary.rejected || 0} | Draft: {summary.draft || 0}
          </div>
        </div>
      </div>

      <div className="apo-table-card">
        <div className="apo-table-head">
          <h6 className="mb-0">PO History</h6>
          <Button size="sm" variant="outline-dark" onClick={load}>Refresh</Button>
        </div>
        {poHistory.length > 0 ? (
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Batch</th>
                  <th>Product</th>
                  <th>Status</th>
                  <th>Total Qty</th>
                  <th>Received</th>
                  <th>Remaining</th>
                  <th>Rate</th>
                  <th>Dispatches</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {poHistory.map((row) => {
                  const active = String(row.poNumber || "") === String(po.poNumber || "");
                  return (
                    <tr
                      key={row.poNumber || row.batchNumber}
                      className={active ? "apo-row-active" : ""}
                      onClick={() => selectPo(row.poNumber || "")}
                      style={{ cursor: "pointer" }}
                      title={row.poNumber ? `Select ${row.poNumber}` : "Select supplier PO"}
                    >
                      <td className="mono">{row.poNumber || "—"}</td>
                      <td className="mono">{row.batchNumber || "—"}</td>
                      <td>{row.productName || "—"}</td>
                      <td>{row.status === "open" ? "Open" : "Closed"}</td>
                      <td>{num(row.totalQty) == null ? "—" : `${Number(row.totalQty)} ${row.unit || unit}`}</td>
                      <td>{num(row.receivedQty) == null ? "—" : `${Number(row.receivedQty)} ${row.unit || unit}`}</td>
                      <td>{num(row.remainingQty) == null ? "—" : `${Number(row.remainingQty)} ${row.unit || unit}`}</td>
                      <td>{num(row.pricePerUnit) == null ? "—" : `₹${Number(row.pricePerUnit).toLocaleString("en-IN")}`}</td>
                      <td>{row.totalDispatches || 0}</td>
                      <td>{fmtIST(row.lastUpdatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : (
          <Alert variant="info" className="m-3">No PO history available.</Alert>
        )}
      </div>

      <div className="apo-table-card">
        <div className="apo-table-head">
          <h6 className="mb-0">Dispatch Entries ({po.poNumber || "Selected PO"})</h6>
          <Button size="sm" variant="outline-dark" onClick={load}>Refresh</Button>
        </div>
        {Array.isArray(data?.dispatches) && data.dispatches.length > 0 ? (
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Material</th>
                  <th>Qty</th>
                  <th>Vehicle</th>
                  <th>Status</th>
                  <th>PO</th>
                  <th>Batch</th>
                </tr>
              </thead>
              <tbody>
                {data.dispatches.map((d) => (
                  <tr key={d._id}>
                    <td>{fmtIST(d.dispatchDateTime || d.submittedAt || d.createdAt)}</td>
                    <td>{d.materialName || "—"}</td>
                    <td>{num(d.quantity) == null ? "—" : `${Number(d.quantity)} ${d.unit || ""}`}</td>
                    <td>{d?.vehicle?.number || "—"}</td>
                    <td>{d.approvalStatus || "draft"}</td>
                    <td className="mono">{d.purchaseOrderNo || "—"}</td>
                    <td className="mono">{d.batchNumber || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <Alert variant="info" className="m-3">No dispatch entries found for this PO.</Alert>
        )}
      </div>
    </div>
  );
};

export default AdminSupplierPoView;
